/**
 * status.ts
 * ----------------
 * Reducers related to status used across all portals.
 */

import { List, Record } from 'immutable';

import { Action, AsyncAction, PayloadAction } from '../../actions/types';
import {
    PreloadStatuses, Shared_AcknowledgeError, Shared_AcknowledgeSuccess, Shared_ReceiveGetStatus,
    Shared_RequestGetStatus,
} from '../../constants/actions';
import { Status } from '../../constants/statuses';
import { GetStatusResponse } from '../../models/dto/UpdatePortal';
import { StatusPerServiceDocument } from '../../models/status';
import { asyncRequestReducer } from '../shared/common';

interface StatusStateFields {
    getStatusError: string;
    getStatusStatus: Status;
    queriedTimestampUtc: string;
    statuses: List<StatusPerServiceDocument>;
}

/**
 * Application state used by applications for prepopulated state.
 */
export class StatusState extends Record<StatusStateFields>({
    getStatusError: '',
    getStatusStatus: 'none',
    queriedTimestampUtc: '',
    statuses: List<StatusPerServiceDocument>(),
}) {}

const initialState = new StatusState();

function clearRequestStateReducer (state: StatusState) {
    return state.set('getStatusError', initialState.getStatusError)
        .set('getStatusStatus', initialState.getStatusStatus);
}

function receiveGetStatusReducer (state: StatusState, action: PayloadAction<GetStatusResponse>) {
    const { payload } = action;
    const { queriedTimestamp, services } = payload;

    return state.set('queriedTimestampUtc', queriedTimestamp)
        .set('statuses', List(services));
}

const requestGetStatusReducer = asyncRequestReducer<StatusState>(
    (state, error) => state.set('getStatusError', error),
    (state, status) => state.set('getStatusStatus', status)
);

/**
 * Reducer that updates application sub-state based on status-related actions.
 * @param state Current application state.
 * @param action Action to be performed on state.
 * @returns Updated application state.
 */
export function statusReducer (state: StatusState = initialState, action?: Action) {
    // Pass back input state if no action given
    if (action === undefined) {
        return state;
    }

    switch (action.type) {
        case Shared_AcknowledgeError:
        case Shared_AcknowledgeSuccess:
            return clearRequestStateReducer(state);

        // Note: we can use the same reducer for both actions since their payloads are equivalent.
        case PreloadStatuses:
        case Shared_ReceiveGetStatus:
            return receiveGetStatusReducer(state, action as PayloadAction<GetStatusResponse>);

        case Shared_RequestGetStatus:
            return requestGetStatusReducer(state, action as AsyncAction);

        default:
            return state;
    }
}
